import { useState, useEffect, useCallback, createContext, useContext } from 'react'
import { authAPI } from '../services/api'
import {
  LOGIN_USER_KEY,
  LOGOUT_REASON_EXPIRED,
  LOGOUT_REASON_IDLE,
  LOGOUT_REASON_KEY,
} from '../services/authKeys'
import { onSessionExpired } from '../services/authEvents'
import toast from 'react-hot-toast'
import { loginErrorMessage } from '../utils/loginErrorMessage'
// The i18next instance itself, as loginErrorMessage: tests mock react-i18next.
import i18n from 'i18next'

const AuthContext = createContext()

// VH-119: the login returns an access token (30-min TTL); renewing it keeps an
// active user from being bounced to /login. VH-284: it names a session on the
// server, which ends 12 hours after the login at the latest.
// VH-304 (SEC-006): the refresh token is an HttpOnly cookie the server sets —
// no script, ours included, can read it. A refresh token a page stored before
// the switch is dropped here.
const persistTokens = (token) => {
  if (token?.access_token) {
    localStorage.setItem('token', token.access_token)
  }
  localStorage.removeItem('refresh_token')
}

const clearTokens = () => {
  localStorage.removeItem('token')
  localStorage.removeItem('refresh_token')
}

const setLogoutReason = (reason) => {
  try {
    if (reason) localStorage.setItem(LOGOUT_REASON_KEY, reason)
    else localStorage.removeItem(LOGOUT_REASON_KEY)
  } catch {
    // Storage refused: the note is a courtesy, the log-out still happens.
  }
}

// VH-293: whose login this is, kept past an ended login (see LOGIN_USER_KEY).
const rememberUser = (user) => {
  try {
    if (user?.id != null) localStorage.setItem(LOGIN_USER_KEY, String(user.id))
    else localStorage.removeItem(LOGIN_USER_KEY)
  } catch {
    // Storage refused: only the way back to the report page is lost.
  }
}

// VH-284: end the session on the server. Its failure is nobody's business:
// the tokens are forgotten either way, and a session that ended already is
// what was wanted.
const endOnServer = async () => {
  if (!localStorage.getItem('token')) return
  try {
    await authAPI.logout()
  } catch {
    // Network down or session gone: nothing more to do.
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) {
      checkAuth()
    } else {
      setLoading(false)
    }
  }, [])

  // VH-119: when a background token refresh ultimately fails (refresh token
  // missing / expired / rejected), services/api.js emits a session-expired
  // event. Clear state here so the app soft-navigates to the login screen —
  // no hard window.location wipe.
  const sessionExpired = useCallback(() => {
    clearTokens()
    setUser(null)
  }, [])

  useEffect(() => {
    const unsubscribe = onSessionExpired(() => {
      setLogoutReason(LOGOUT_REASON_EXPIRED)
      clearTokens()
      setUser(null)
      toast.error(i18n.t('auth.sessionExpired'))
    })
    return unsubscribe
  }, [])

  // VH-284: the login is one for all tabs. When another tab logs out (the
  // button, the idle countdown, a refused refresh), this one follows.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === 'token' && !event.newValue) setUser(null)
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // VH-284: the idle countdown reached zero, or "Log out now" in the warning.
  const endAfterIdle = useCallback(async () => {
    setLogoutReason(LOGOUT_REASON_IDLE)
    await endOnServer()
    clearTokens()
    setUser(null)
  }, [])

  const checkAuth = async () => {
    try {
      const response = await authAPI.getProfile()
      rememberUser(response.data)
      setUser(response.data)
    } catch (error) {
      // VH-293: only a refusal ends the login. Without any answer (network
      // down) the tokens stay for the next try, and nothing claims a log-out.
      if (error?.isAxiosError && !error.response) return
      if (localStorage.getItem('token') || localStorage.getItem('refresh_token')) {
        setLogoutReason(LOGOUT_REASON_EXPIRED)
      }
      clearTokens()
      toast.error(i18n.t('auth.sessionExpired'))
    } finally {
      setLoading(false)
    }
  }

  // VH-119: update stored tokens after a silent refresh (exposed for callers
  // that need to persist a freshly rotated token pair).
  const updateTokens = useCallback((token) => {
    persistTokens(token)
  }, [])

  // VH-285: the password step. No tokens yet: the answer is a ticket for the
  // code (or the first set-up), which the login page carries on with.
  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials)
      return { success: true, step: response.data }
    } catch (error) {
      const message = loginErrorMessage(error)
      toast.error(message)
      return { success: false, error: message }
    }
  }

  // VH-285: the code step (or the end of a first set-up) answered the login.
  const finishLogin = ({ token, user }) => {
    persistTokens(token)
    setLogoutReason(null)
    rememberUser(user)
    setUser(user)
    toast.success(i18n.t('secondFactor.login.welcome'))
  }

  const logout = async () => {
    await endOnServer()
    clearTokens()
    // VH-293: a chosen log-out is not "your login ended": no bar, no way back.
    setLogoutReason(null)
    rememberUser(null)
    setUser(null)
    toast.success('Logged out successfully')
  }

  const updateProfile = async (data) => {
    try {
      const response = await authAPI.updateProfile(data)
      setUser(response.data)
      toast.success('Profile updated successfully!')
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.detail || 'Profile update failed'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const value = {
    user,
    loading,
    login,
    finishLogin,
    logout,
    sessionExpired,
    endAfterIdle,
    updateTokens,
    updateProfile,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components -- hook must be co-located with its provider
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

/**
 * VH-146: auth state for components that must survive WITHOUT a provider.
 *
 * The public capture page (`/capture/...`) is rendered before the auth gate on
 * purpose, so an unauthenticated visitor arriving from a printed QR is never
 * bounced to /login. It happens to sit inside AuthProvider today, but throwing
 * if that ever changes would take down the one page whose entire premise is
 * working without a login. Returning null is the honest answer to "who is
 * signed in?" when nobody is.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook must be co-located with its provider
export const useOptionalAuth = () => useContext(AuthContext) ?? { user: null }